import React from 'react'
import { useNavigate } from 'react-router-dom'
import './Account.css'
import { useStructuraAuth } from './useStructuraAuth'
import { useAccountInfo, AccountInfo } from './useAccountInfo'
import CooperativeUsersManagement from './CooperativeUsersManagement'
import { apiConfig } from '@structura/services'
import { useTranslation } from '@structura/i18n'

export interface AccountPageProps {
  // Display label for the current app (e.g. "Wood", "Steel", "Concrete").
  // Forwarded to CooperativeUsersManagement to make per-app cooperator
  // scoping visible in the UI. See docs/scratch/cooperative-users-design.md.
  appLabel?: string
}

const formatDate = (value: string | undefined): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  }).format(date)
}

const Account: React.FC<AccountPageProps> = ({ appLabel }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const authState = useStructuraAuth()
  const accountState = useAccountInfo(authState)
  
  // Login Form State
  const [loginEmail, setLoginEmail] = React.useState<string>('')
  const [loginPassword, setLoginPassword] = React.useState<string>('')
  const [loginError, setLoginError] = React.useState<string | null>(null)

  // Edit Profile State
  const [editingSection, setEditingSection] = React.useState<'none' | 'personal' | 'company'>('none')
  const [isSaving, setIsSaving] = React.useState(false)
  const [editForm, setEditForm] = React.useState<Partial<AccountInfo>>({})
  const [saveMessage, setSaveMessage] = React.useState<{type: 'success'|'error', text: string} | null>(null)

  // Upload state
  const [avatarUploadError, setAvatarUploadError] = React.useState<string | null>(null)
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false)
  const [logoUploadError, setLogoUploadError] = React.useState<string | null>(null)
  const [uploadingLogo, setUploadingLogo] = React.useState(false)

  const handleLoginSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setLoginError(null)
    try {
      await authState.login(loginEmail, loginPassword)
      navigate('/')
    } catch (error) {
      const message: string = error instanceof Error ? error.message : t('auth.account.errLogin')
      setLoginError(message)
    }
  }

  const startEditing = (section: 'personal' | 'company', account: AccountInfo): void => {
    setSaveMessage(null)
    if (section === 'personal') {
        setEditForm({
            name: account.name,
            competence: account.competence,
            avatarUrl: account.avatarUrl
        })
    } else {
        setEditForm({
            portalId: account.portalId,
            companyName: account.companyName,
            logoUrl: account.logoUrl,
            companyPitch: account.companyPitch,
            contactEmail: account.contactEmail,
            hqLocation: account.hqLocation,
            serviceKeywords: account.serviceKeywords,
            sectorFocus: account.sectorFocus,
            professionalCertifications: account.professionalCertifications,
            role: account.role
        })
    }
    setEditingSection(section)
  }

  const cancelEditing = () => {
      setEditingSection('none')
      setEditForm({})
      setSaveMessage(null)
  }

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setEditForm(prev => ({ ...prev, [name]: value }))
  }

  const saveProfile = async (): Promise<void> => {
    setIsSaving(true)
    setSaveMessage(null)
    try {
      const token = await authState.getAccessToken()
      const response = await fetch(`${apiConfig.baseUrl}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      })

      if (!response.ok) {
        throw new Error(t('auth.account.errUpdateProfile'))
      }

      setSaveMessage({ type: 'success', text: t('auth.account.profileUpdated') })

      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      setSaveMessage({ type: 'error', text: error instanceof Error ? error.message : t('auth.account.updateFailed') })
      setIsSaving(false)
    }
  }

  const uploadAvatar = async (file: File): Promise<void> => {
    setAvatarUploadError(null)
    setUploadingAvatar(true)
    try {
      const token: string = await authState.getAccessToken()
      const formData: FormData = new FormData()
      formData.append('file', file)

      const response: Response = await fetch(`${apiConfig.baseUrl}/api/user/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (!response.ok) {
        const text: string = await response.text()
        throw new Error(t('auth.account.errUploadAvatar', { status: response.status, text }))
      }

      const payload: any = await response.json()
      const url: string = payload.avatarUrl ?? payload.data?.avatarUrl ?? ''
      if (url.length > 0) {
        setEditForm(prev => ({ ...prev, avatarUrl: url }))
      }
    } catch (error) {
      const message: string = error instanceof Error ? error.message : t('auth.account.errUploadAvatarGeneric')
      setAvatarUploadError(message)
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleAvatarFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!event.target.files || event.target.files.length === 0) {
      return
    }
    const file: File = event.target.files[0]
    await uploadAvatar(file)
  }

  const uploadLogo = async (file: File): Promise<void> => {
    setLogoUploadError(null)
    setUploadingLogo(true)
    try {
      const token: string = await authState.getAccessToken()
      const formData: FormData = new FormData()
      formData.append('file', file)

      const response: Response = await fetch(`${apiConfig.baseUrl}/api/user/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      if (!response.ok) {
        const text: string = await response.text()
        throw new Error(t('auth.account.errUploadLogo', { status: response.status, text }))
      }

      const payload: any = await response.json()
      const url: string = payload.logoUrl ?? payload.data?.logoUrl ?? ''
      if (url.length > 0) {
        setEditForm(prev => ({ ...prev, logoUrl: url }))
      }
    } catch (error) {
      const message: string = error instanceof Error ? error.message : t('auth.account.errUploadLogoGeneric')
      setLogoUploadError(message)
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!event.target.files || event.target.files.length === 0) {
      return
    }
    const file: File = event.target.files[0]
    await uploadLogo(file)
  }

  if (!authState.isAuthenticated) {
    return (
      <div className="account-page">
        <div className="signin-hero">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 col-md-7 col-sm-10">
                <div className="signin-card">
                  <div className="signin-logo">
                    <i className="bi bi-building"></i>
                  </div>
                  <h1 className="signin-title text-center">{t('auth.account.welcomeBack')}</h1>
                  <p className="signin-subtitle text-center mb-4">{t('auth.account.signInSubtitle')}</p>
                  <form className="signin-form" onSubmit={handleLoginSubmit}>
                    <div className="mb-3">
                      <label htmlFor="accountLoginEmail" className="form-label">{t('auth.account.emailAddress')}</label>
                      <input id="accountLoginEmail" type="email" className="form-control" placeholder="you@company.com" value={loginEmail} onInput={(event) => setLoginEmail((event.target as HTMLInputElement).value)} onChange={(event) => setLoginEmail(event.target.value)} required />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="accountLoginPassword" className="form-label">{t('auth.account.password')}</label>
                      <input id="accountLoginPassword" type="password" className="form-control" placeholder={t('auth.account.passwordPlaceholder')} value={loginPassword} onInput={(event) => setLoginPassword((event.target as HTMLInputElement).value)} onChange={(event) => setLoginPassword(event.target.value)} required />
                    </div>
                    <div className="mb-4 text-end">
                      <a href="/forgot-password" className="small text-decoration-none" style={{ color: 'forestgreen' }}>
                        {t('auth.forgot.title')}
                      </a>
                    </div>
                    {(loginError !== null || (authState.errorMessage !== undefined && authState.errorMessage.length > 0)) && (
                      <div className="alert alert-danger signin-alert" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {loginError ?? authState.errorMessage}
                      </div>
                    )}
                    <div className="d-grid">
                      <button type="submit" className="signin-btn" disabled={authState.isLoading || loginEmail.length === 0 || loginPassword.length === 0}>
                        {authState.isLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('auth.account.signingIn')}</> : <><i className="bi bi-box-arrow-in-right me-2"></i>{t('auth.nav.signIn')}</>}
                      </button>
                    </div>
                  </form>
                  <div className="signin-divider"><span>{t('auth.account.newToStructura')}</span></div>
                  <p className="signin-register-link">
                    <a href="/register">{t('auth.account.createAccountLink')}</a>{t('auth.account.createAccountRest')}
                  </p>
                  <div className="signin-features">
                    <div className="signin-feature">
                      <span className="signin-feature-icon"><i className="bi bi-check-lg"></i></span>
                      <span>{t('auth.account.featAccessTools')}</span>
                    </div>
                    <div className="signin-feature">
                      <span className="signin-feature-icon"><i className="bi bi-check-lg"></i></span>
                      <span>{t('auth.account.featManageSub')}</span>
                    </div>
                    <div className="signin-feature">
                      <span className="signin-feature-icon"><i className="bi bi-check-lg"></i></span>
                      <span>{t('auth.account.featDownloadReports')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (accountState.status === 'loading' || accountState.status === 'idle') return <div className="account-page"><div className="container py-5 d-flex justify-content-center"><div className="spinner-border text-primary"></div></div></div>
  if (accountState.status === 'error') return <div className="account-page"><div className="container py-5"><div className="alert alert-danger">{accountState.error}</div></div></div>
  if (accountState.status === 'unavailable') return <div className="account-page"><div className="container py-5"><div className="alert alert-warning">{t('auth.account.unavailable')}</div></div></div>
  if (accountState.status !== 'loaded') return null

  const { account } = accountState

  return (
    <div className="account-container">
      {/* Header - Structura Bits Style */}
      <div className="account-header">
        <div className="account-title-section">
          <h1 className="account-main-title">
            <span className="sparkle-icon">👤</span>
            {t('auth.nav.myAccount')}
          </h1>
          <p className="account-subtitle">
            {t('auth.account.manageSubtitle')}
          </p>
        </div>
      </div>

      <div className="account-content">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            
            {/* Personal Info Card */}
            <div className="account-card mb-4">
               <div className="card-header bg-transparent border-0 pt-4 ps-4 pe-4 d-flex justify-content-between align-items-center">
                 <h5 className="fw-bold mb-0 text-dark-green"><i className="bi bi-person me-2"></i>{t('auth.account.personalInfo')}</h5>
                 {editingSection !== 'personal' && (
                  <button className="btn btn-outline-success btn-sm" onClick={() => startEditing('personal', account)} disabled={editingSection !== 'none'}>
                    <i className="bi bi-pencil me-1"></i> {t('auth.account.edit')}
                  </button>
                 )}
               </div>
               <div className="card-body p-4">
                 {editingSection === 'personal' ? (
                    <div className="edit-form">
                        {saveMessage && (
                            <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} py-2 mb-3`}>{saveMessage.text}</div>
                        )}
                        <div className="mb-3">
                        <div className="text-center mb-4">
                          <div className="position-relative d-inline-block">
                            {editForm.avatarUrl ? (
                              <img src={editForm.avatarUrl.startsWith('http') ? editForm.avatarUrl : `${apiConfig.baseUrl}${editForm.avatarUrl}`} alt={t('auth.account.avatarPreviewAlt')} className="rounded-circle object-fit-cover" style={{width: '100px', height: '100px'}} />
                            ) : (
                              <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center text-muted" style={{width: '100px', height: '100px'}}>
                                <i className="bi bi-person fs-1"></i>
                              </div>
                            )}
                          </div>
                          <div className="mt-2">
                            <label className="form-label small text-muted">{t('auth.account.uploadPhoto')}</label>
                            <input type="file" className="form-control form-control-sm" accept="image/*" onChange={handleAvatarFileChange} disabled={uploadingAvatar} />
                            {avatarUploadError && <div className="text-danger small mt-1">{avatarUploadError}</div>}
                          </div>
                          <div className="mt-2">
                            <label className="form-label small text-muted">{t('auth.account.avatarUrlOptional')}</label>
                            <input type="text" className="form-control form-control-sm" name="avatarUrl" value={editForm.avatarUrl || ''} onChange={handleEditChange} placeholder="https://..." />
                          </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label small text-muted">{t('auth.account.yourName')}</label>
                            <input type="text" className="form-control" name="name" value={editForm.name || ''} onChange={handleEditChange} />
                        </div>

                        <div className="mb-3">
                            <label className="form-label small text-muted">{t('auth.account.email')}</label>
                            <input type="email" className="form-control" value={account.email} disabled readOnly />
                        </div>

                        <div className="mb-3">
                            <label className="form-label small text-muted">{t('auth.account.competenceSkills')}</label>
                            <textarea className="form-control" name="competence" rows={3} value={editForm.competence || ''} onChange={handleEditChange} placeholder={t('auth.account.competencePlaceholder')} />
                        </div>
                        </div>
                        <div className="d-flex gap-2 mt-4">
                            <button className="btn btn-success flex-grow-1" onClick={saveProfile} disabled={isSaving}>{isSaving ? t('auth.account.saving') : t('auth.account.saveChanges')}</button>
                            <button className="btn btn-light" onClick={cancelEditing} disabled={isSaving}>{t('auth.common.cancel')}</button>
                        </div>
                    </div>
                 ) : (
                    <div className="d-flex flex-column flex-md-row gap-4 align-items-center align-items-md-start">
                      <div className="flex-shrink-0 text-center">
                        {account.avatarUrl ? (
                          <img src={account.avatarUrl.startsWith('http') ? account.avatarUrl : `${apiConfig.baseUrl}${account.avatarUrl}`} alt={account.name} className="rounded-circle object-fit-cover shadow-sm" style={{width: '120px', height: '120px'}} />
                        ) : (
                          <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center text-muted shadow-sm" style={{width: '120px', height: '120px'}}>
                            <i className="bi bi-person fs-1"></i>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-grow-1 w-100">
                        <dl className="row g-0 mb-0">
                          <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.name')}</dt>
                          <dd className="col-sm-8 mb-2 fw-medium text-dark">{account.name || '—'}</dd>

                          <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.email')}</dt>
                          <dd className="col-sm-8 mb-2 fw-medium text-dark">{account.email}</dd>

                          <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.competence')}</dt>
                          <dd className="col-sm-8 mb-2 fw-medium text-dark">{account.competence || '—'}</dd>
                        </dl>
                      </div>
                    </div>
                 )}
               </div>
            </div>

            {/* Company Profile Card */}
            <div className="account-card">
              <div className="card-header bg-transparent border-0 pt-4 ps-4 pe-4 d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-0 text-dark-green">
                  <i className="bi bi-building me-2"></i>
                  {t('auth.account.companyProfile')}
                </h5>
                {editingSection !== 'company' && (
                  <button className="btn btn-outline-success btn-sm" onClick={() => startEditing('company', account)} disabled={editingSection !== 'none'}>
                    <i className="bi bi-pencil me-1"></i> {t('auth.account.edit')}
                  </button>
                )}
              </div>
              <div className="card-body p-4">
                {editingSection === 'company' ? (
                  <div className="edit-form">
                    {saveMessage && (
                      <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} py-2 mb-3`}>{saveMessage.text}</div>
                    )}
                    
                    <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.companyLogo')}</label>
                      <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                        <div className="flex-shrink-0 text-center">
                          {editForm.logoUrl || account.logoUrl ? (
                            <img src={(editForm.logoUrl || account.logoUrl)!.startsWith('http') ? (editForm.logoUrl || account.logoUrl) : `${apiConfig.baseUrl}${editForm.logoUrl || account.logoUrl}`} alt={t('auth.account.companyLogoPreviewAlt')} className="img-fluid" style={{maxHeight: '60px'}} />
                          ) : (
                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center text-muted" style={{width: '60px', height: '60px'}}>
                              <i className="bi bi-building fs-4"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-grow-1 w-100">
                          <input type="file" className="form-control form-control-sm" accept="image/*" onChange={handleLogoFileChange} disabled={uploadingLogo} />
                          {logoUploadError && <div className="text-danger small mt-1">{logoUploadError}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.companyName')}</label>
                      <input type="text" className="form-control" name="companyName" value={editForm.companyName || ''} onChange={handleEditChange} />
                    </div>

                    <div className="mb-3 bg-light p-3 rounded">
                       <label className="form-label small text-muted mb-1">{t('auth.account.portalIdInternal')}</label>
                       <div className="d-flex">
                         <input type="text" className="form-control" name="portalId" value={editForm.portalId || ''} onChange={handleEditChange} />
                       </div>
                       <div className="form-text small">{t('auth.account.portalIdHint')}</div>
                    </div>

                    <div className="row g-2">
                      <div className="col-12 mb-3">
                         <label className="form-label small text-muted">{t('auth.account.contactEmail')}</label>
                         <input type="text" className="form-control" name="contactEmail" value={editForm.contactEmail || ''} onChange={handleEditChange} />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.hqLocation')}</label>
                      <input type="text" className="form-control" name="hqLocation" value={editForm.hqLocation || ''} onChange={handleEditChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.companyPitch')}</label>
                      <input type="text" className="form-control" name="companyPitch" value={editForm.companyPitch || ''} onChange={handleEditChange} placeholder={t('auth.account.companyPitchPlaceholder')} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.sectorFocus')}</label>
                      <input type="text" className="form-control" name="sectorFocus" value={editForm.sectorFocus || ''} onChange={handleEditChange} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.serviceKeywords')}</label>
                      <input type="text" className="form-control" name="serviceKeywords" value={editForm.serviceKeywords || ''} onChange={handleEditChange} />
                    </div>
                     <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.certifications')}</label>
                      <input type="text" className="form-control" name="professionalCertifications" value={editForm.professionalCertifications || ''} onChange={handleEditChange} />
                    </div>

                    <div className="mb-3">
                      <label className="form-label small text-muted">{t('auth.account.role')}</label>
                      <select className="form-select" name="role" value={editForm.role || 'Designer'} onChange={handleEditChange}>
                        <option value="Designer">{t('auth.coop.roleDesigner')}</option>
                        <option value="Approval">{t('auth.coop.roleApproval')}</option>
                        <option value="Info">{t('auth.coop.roleInfo')}</option>
                        <option value="All">{t('auth.coop.roleAll')}</option>
                      </select>
                    </div>

                    <div className="d-flex gap-2 mt-4">
                      <button className="btn btn-success flex-grow-1" onClick={saveProfile} disabled={isSaving}>{isSaving ? t('auth.account.saving') : t('auth.account.saveChanges')}</button>
                      <button className="btn btn-light" onClick={cancelEditing} disabled={isSaving}>{t('auth.common.cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <div className="profile-view">
                    <div className="mb-4 text-center">
                      {account.logoUrl ? (
                        <img src={account.logoUrl.startsWith('http') ? account.logoUrl : `${apiConfig.baseUrl}${account.logoUrl}`} alt={t('auth.account.companyLogoAlt')} className="img-fluid mb-3" style={{maxHeight: '80px'}} />
                      ) : (
                        <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3 text-muted" style={{width: '80px', height: '80px'}}>
                          <i className="bi bi-building fs-2 text-success"></i>
                        </div>
                      )}
                      {account.companyName && <h4 className="h5 fw-bold text-dark-green">{account.companyName}</h4>}
                      {account.hqLocation && <div className="text-muted small"><i className="bi bi-geo-alt me-1"></i>{account.hqLocation}</div>}
                    </div>

                    <dl className="row g-0">
                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.dtPortalId')}</dt>
                      <dd className="col-sm-8 mb-2 fw-medium"><span className="badge bg-light text-dark border">{account.portalId || '—'}</span></dd>

                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.companyPitch')}</dt>
                      <dd className="col-sm-8 mb-2 fw-medium text-dark">{account.companyPitch || '—'}</dd>

                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.dtContact')}</dt>
                      <dd className="col-sm-8 mb-2 fw-medium text-dark">{account.contactEmail || account.email}</dd>

                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.dtFocus')}</dt>
                      <dd className="col-sm-8 mb-2 fw-medium text-dark">{account.sectorFocus || '—'}</dd>

                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.dtServices')}</dt>
                      <dd className="col-sm-8 mb-2 small text-muted">{account.serviceKeywords || '—'}</dd>

                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.certifications')}</dt>
                      <dd className="col-sm-8 mb-2 small text-muted">{account.professionalCertifications || '—'}</dd>

                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.role')}</dt>
                      <dd className="col-sm-8 mb-2 fw-medium text-dark">{account.role || 'Designer'}</dd>

                      <dt className="col-sm-4 text-muted small fw-normal mb-2">{t('auth.account.dtSubscription')}</dt>
                      <dd className="col-sm-8 mb-2 fw-medium text-success">{t('auth.account.tierPlan', { tier: account.tier })}</dd>
                    </dl>
                    <hr className="my-4 border-light" />
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">{t('auth.account.memberSince')}</small>
                      <span className="fw-medium text-dark">{t('auth.account.renew', { date: formatDate(account.subscriptionExpiresAt) })}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Cooperative Users Management */}
            <CooperativeUsersManagement authToken={authState.getAccessToken} appLabel={appLabel} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Account
